import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import "./Header.scss";
import { slide as BurgerMenu } from "react-burger-menu";
import { TextWithIcon } from "./TextWithIcon/TextWithIcon";
import { Link, useNavigate } from "react-router-dom";
import { Input, Type } from "../Input/Input";
import { API_BASE_URL, ICONPATH, WS_BASE_URI } from "../../Constant";
import { DropDown } from "./DropDown/DropDown";
import { Logo } from "../Logo/Logo";
import { Menu } from "../Menu/Menu";
import { MENU_DATA } from "../Menu/menuData";
import { ROUTES } from "../../../data";
import { SelectLanguage } from "./DropDown/SelectLanguage";
import { getLocale } from "../../../redux/action/localeAction";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";
import { Font } from "../Fonts/Font";
import ReactFlagsSelect from "react-flags-select";
import { toast, ToastContainer } from "react-toastify";
import { alertActions, userActions } from "../../../redux/action";
import { connect, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { isAdminUser, getAdminRoleFromAuth, getDefaultAdminRoute } from "../../../helpers/adminRoles";
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket";
import { ReadyState } from "react-use-websocket";
import { NotificationsDropdown } from "./NotificationsDropdown/NotificationsDropdown";

/**
 * Recent searches helpers -- stores up to 5 recent search terms in localStorage.
 */
const RECENT_SEARCHES_KEY = "ef_recent_searches";
const MAX_RECENT_SEARCHES = 5;

const getRecentSearches = () => {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const addRecentSearch = (query) => {
  if (!query || !query.trim()) return;
  const trimmed = query.trim();
  let recent = getRecentSearches().filter((s) => s !== trimmed);
  recent.unshift(trimmed);
  if (recent.length > MAX_RECENT_SEARCHES) recent = recent.slice(0, MAX_RECENT_SEARCHES);
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
  } catch { /* quota exceeded -- ignore */ }
};

/**
 * Custom hook for search autocomplete with debounced API calls.
 * Returns suggestions state and handlers to wire into any search input.
 */
const useSearchAutocomplete = (navigate) => {
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);
  const currentQueryRef = useRef("");

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  const fetchSuggestions = useCallback(
    (query) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      currentQueryRef.current = query;

      if (query.length < 2) {
        setSuggestions([]);
        // Show recent searches when input is focused but too short for API call
        if (query.length === 0) {
          setRecentSearches(getRecentSearches());
          setShowSuggestions(getRecentSearches().length > 0);
        } else {
          setShowSuggestions(false);
        }
        return;
      }

      debounceRef.current = setTimeout(() => {
        fetch(
          `${API_BASE_URL}/products/suggestions/?q=${encodeURIComponent(query)}`
        )
          .then((res) => res.json())
          .then((data) => {
            const items = Array.isArray(data) ? data : [];
            setSuggestions(items);
            setShowSuggestions(items.length > 0 || getRecentSearches().length > 0);
          })
          .catch(() => {
            setSuggestions([]);
            setShowSuggestions(getRecentSearches().length > 0);
          });
      }, 300);
    },
    []
  );

  const handleSuggestionClick = useCallback(
    (item) => {
      setShowSuggestions(false);
      setSuggestions([]);
      if (item.type === "recent") {
        // Navigate to search with the recent term
        addRecentSearch(item.title);
        navigate(ROUTES.SEARCH + `/?q=${encodeURIComponent(item.title)}`);
      } else if (item.type === "product") {
        addRecentSearch(item.title);
        navigate(ROUTES.PRODUCTSINGLE + "/" + item.slug);
      } else if (item.type === "category") {
        navigate(ROUTES.CATEGORY + "/" + item.slug);
      }
    },
    [navigate]
  );

  const closeSuggestions = useCallback(() => {
    // Small delay so click on suggestion registers before dropdown hides
    setTimeout(() => setShowSuggestions(false), 150);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  }, []);

  // Save search term when user navigates
  const saveRecentSearch = useCallback((query) => {
    if (query && query.trim()) {
      addRecentSearch(query.trim());
      setRecentSearches(getRecentSearches());
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    suggestions,
    recentSearches,
    showSuggestions,
    dropdownRef,
    fetchSuggestions,
    handleSuggestionClick,
    closeSuggestions,
    handleKeyDown,
    saveRecentSearch,
  };
};

/**
 * Renders the autocomplete suggestions dropdown with recent searches.
 */
const SuggestionsDropdown = React.memo(function SuggestionsDropdown({ suggestions, recentSearches, onSelect, dropdownRef, t }) {
  const categories = suggestions.filter((s) => s.type === "category");
  const products = suggestions.filter((s) => s.type === "product");
  const recent = recentSearches || [];

  return (
    <div className="search-autocomplete-dropdown" ref={dropdownRef}>
      {/* Recent Searches */}
      {recent.length > 0 && suggestions.length === 0 && (
        <>
          <div className="search-autocomplete-header search-autocomplete-header--recent">
            {t("header.recentSearches", "Recent Searches")}
          </div>
          {recent.map((term, idx) => (
            <div
              key={`recent-${idx}`}
              className="search-autocomplete-item search-autocomplete-item--recent"
              onMouseDown={() => onSelect({ type: "recent", title: term })}
            >
              <span className="search-recent-icon">&#128337;</span>
              {term}
            </div>
          ))}
        </>
      )}
      {categories.length > 0 && (
        <>
          <div className="search-autocomplete-header">{t("header.categories")}</div>
          {categories.map((item, idx) => (
            <div
              key={`cat-${idx}`}
              className="search-autocomplete-item"
              onMouseDown={() => onSelect(item)}
            >
              {item.title}
            </div>
          ))}
        </>
      )}
      {products.length > 0 && (
        <>
          <div className="search-autocomplete-header">{t("header.products")}</div>
          {products.map((item, idx) => (
            <div
              key={`prod-${idx}`}
              className="search-autocomplete-item"
              onMouseDown={() => onSelect(item)}
            >
              {item.title}
            </div>
          ))}
        </>
      )}
    </div>
  );
});

export const DeskopHeader = ({
  click,
  show,
  menuData,
  isLoggedIn,
  ...props
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState("");
  const {
    suggestions,
    recentSearches,
    showSuggestions,
    dropdownRef,
    fetchSuggestions,
    handleSuggestionClick,
    closeSuggestions,
    handleKeyDown,
    saveRecentSearch,
  } = useSearchAutocomplete(navigate);
  const user = useSelector((el) => el?.users?.profile?.name);
  const userToken = useSelector((el) => el?.authentication?.user);

  let tokenData = userToken?.jwt_token?.split(".")[1];
  let isSellerVerified;
  if (tokenData) {
    isSellerVerified = JSON.parse(atob(tokenData));
  }
  const isUserAdmin = isAdminUser(userToken);
  const adminRole = getAdminRoleFromAuth(userToken);
  const adminDefaultRoute = isUserAdmin ? getDefaultAdminRoute(adminRole) : null;
  const { t } = useTranslation();

  const links = [
    { title: t("header.myAccount"), link: ROUTES.ACCOUNT },
    { title: t("header.order"), link: ROUTES.BUYER_ORDER },
    { title: t("header.inbox"), link: ROUTES.INBOX },
    { title: t("header.savedItems"), link: ROUTES.SAVEDITEM },
    {
      title: t("header.myDashboard"),
      link: isSellerVerified?.seller_verified
        ? ROUTES.SELLER_DASHBOARD
        : ROUTES.BUYER_DASHBOARD,
      isSeller: true,
    },
    ...(isUserAdmin
      ? [{ title: t("header.adminPanel"), link: adminDefaultRoute }]
      : []),
    { title: t("header.logout"), link: ROUTES.LOGIN },
  ];
  const getNameInitial = (name) => {
    name = name?.split(" ");
    let initial = name ? name[0][0] + name[1][0] : "";
    return initial;
  };

  const handleQuerySearch = () => {
    if (queryParams.length > 0) {
      saveRecentSearch(queryParams);
      dispatch(userActions.searchQuery(queryParams));
      navigate(ROUTES.SEARCH + `/?q=${queryParams}`);
    }
  };
  const handleLinkRouteChange = (e) => {
    let route = links.find((el) => el.title === e.value);
    if (route.title === "Logout") {
      dispatch(userActions.logout());
    }
    navigate(route.link);
  };
  const [value, setValue] = useState("Profile");
  const [isHover, setHover] = useState(false);
  const { language, currency } = getLocale().payload?.locale || {
    language: "English",
    currency: "USD",
  };
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const bellRef = useRef(null);
  const { readyState } = useWebSocket(
    userToken ? `${WS_BASE_URI}/notifications/` : null,
    {
      queryParams: {
        token: userToken ? userToken.jwt_token : "",
      },
      onOpen: () => {
      },
      onClose: () => {
      },
      onMessage: (e) => {
        const data = JSON.parse(e.data);
        switch (data.type) {
          case "unread_count":
            setUnreadMessageCount(data.unread_count);
            break;
          case "new_message_notification":
            setUnreadMessageCount((count) => (count += 1));
            break;
          case "unread_notification_count":
            setUnreadNotificationCount(data.unread_notification_count);
            break;
          case "new_notification":
            setUnreadNotificationCount((count) => count + 1);
            break;
          default:
            console.error("Unknown message type!");
            break;
        }
      },
    }
  );
  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState];
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleQuerySearch();
      }
    };
    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  });
  useEffect(() => {
    if (userToken) dispatch(userActions.getUserProfile());
    setValue("Profile");
  }, [dispatch, userToken]);

  return (
    <>
      <div className={["es-custom-header"].join(" ")}>
        <div className="menu-tab">
          <Link to="/">
            <Logo />
          </Link>
        </div>
        <div className="label-section" style={{ display: "flex" }}>
          <div className="search" style={{ position: "relative" }}>
            <Input
              placeholder={t("header.searchPlaceholder")}
              type={Type.WITH_ICON}
              icon={ICONPATH.Filter}
              onChange={(e) => {
                setQueryParams(e.target.value);
                fetchSuggestions(e.target.value);
              }}
              onClick={() => handleQuerySearch()}
              onBlur={closeSuggestions}
              onKeyDown={handleKeyDown}
            />
            {showSuggestions && (suggestions.length > 0 || recentSearches.length > 0) && (
              <SuggestionsDropdown
                suggestions={suggestions}
                recentSearches={recentSearches}
                onSelect={handleSuggestionClick}
                dropdownRef={dropdownRef}
                t={t}
              />
            )}
          </div>
          <div onClick={() => setHover(!isHover)} style={{ marginTop: "9px" }}>
            <TextWithIcon
              icon={ICONPATH.ChevronDown}
              label={`${language} - ${currency}`}
              type={"left"}
              color={"#fff"}
              clickable
              link={"#"}
            />
          </div>

          <TextWithIcon
            icon={ICONPATH.helpCircleIcon}
            label={t("header.help")}
            type={"right"}
            link={"#"}
            color={"#fff"}
            clickable
          />
          <div className="xyz">
            <span style={{ position: "relative" }}>
              <i className="fa fa-envelope" aria-hidden="true"></i>
              {!!unreadMessageCount && (
                <span className="unread-badge">
                  {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                </span>
              )}
            </span>
            <TextWithIcon
              hasNoIcon
              label={t("header.message")}
              type={"right"}
              link={ROUTES.MESSAGE}
              color={"#fff"}
              clickable
            />
          </div>

          <div className="xyz" style={{ position: "relative" }} ref={bellRef}>
            <span
              style={{ position: "relative", cursor: "pointer" }}
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <i className="fa fa-bell" aria-hidden="true"></i>
              {!!unreadNotificationCount && (
                <span className="unread-badge">
                  {unreadNotificationCount > 99
                    ? "99+"
                    : unreadNotificationCount}
                </span>
              )}
            </span>
            <NotificationsDropdown
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
              userToken={userToken}
              onUnreadCountUpdate={setUnreadNotificationCount}
            />
          </div>

          <TextWithIcon
            icon={ICONPATH.cartIcon}
            label={t("cart")}
            type={"right"}
            color={"#fff"}
            link={ROUTES.CART}
            hasBadge
            clickable
          />
          {isLoggedIn ? (
            <div style={{ display: "flex", gap: 10 }} className="prof">
              <div className="imgconst">{getNameInitial(user)}</div>
              <Dropdown
                options={links?.map((el) => el?.title)}
                placeholder={t("header.profile")}
                value={value === "Profile" ? t("header.profile") : value}
                onChange={handleLinkRouteChange}
              />
            </div>
          ) : (
            <TextWithIcon
              icon={ICONPATH.userIcon}
              label={t("login")}
              type={"right"}
              color={"#fff"}
              link={ROUTES.LOGIN}
              clickable
            />
          )}
        </div>
      </div>
      <div className="isMenu">
        <Menu menuData={MENU_DATA} />
      </div>
      <div>
        <SelectLanguage onHover={isHover} close={() => setHover(false)} />
      </div>
    </>
  );
};

export const MobileHeader = ({ isLoggedIn, onClick }) => {
  const { t } = useTranslation();
  const geoLocation = useSelector((el) => el?.geoLocation);
  const [country, setCountry] = useState(geoLocation?.countryCode || "NG");
  const [queryParams, setQueryParams] = useState("");
  const navigate = useNavigate();
  const userToken = useSelector((el) => el?.authentication?.user);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const mobileBellRef = useRef(null);
  const {
    suggestions,
    recentSearches,
    showSuggestions,
    dropdownRef,
    fetchSuggestions,
    handleSuggestionClick,
    closeSuggestions,
    handleKeyDown,
    saveRecentSearch,
  } = useSearchAutocomplete(navigate);

  useWebSocket(
    userToken ? `${WS_BASE_URI}/notifications/` : null,
    {
      queryParams: {
        token: userToken ? userToken.jwt_token : "",
      },
      onOpen: () => {
      },
      onClose: () => {
      },
      onMessage: (e) => {
        const data = JSON.parse(e.data);
        switch (data.type) {
          case "unread_count":
            setUnreadMessageCount(data.unread_count);
            break;
          case "new_message_notification":
            setUnreadMessageCount((count) => (count += 1));
            break;
          case "unread_notification_count":
            setUnreadNotificationCount(data.unread_notification_count);
            break;
          case "new_notification":
            setUnreadNotificationCount((count) => count + 1);
            break;
          default:
            break;
        }
      },
    }
  );
  const handleQuerySearch = () => {
    if (queryParams.length > 0) {
      saveRecentSearch(queryParams);
      dispatch(userActions.searchQuery(queryParams));
      navigate(ROUTES.SEARCH + `/?q=${queryParams}`);
    }
  };
  const dispatch = useDispatch();
  const user = useSelector((el) => el?.authentication?.user);
  const MenuA = useSelector((el) => el?.productMeta?.data?.categories);
  const catergoryMenu = useMemo(() => MenuA?.filter((el) => el.parent === -1), [MenuA]);
  let tokenData = user?.jwt_token?.split(".")[1];
  let isSellerVerified;
  if (tokenData) {
    isSellerVerified = JSON.parse(atob(tokenData));
  }
  const isAuth = !!isSellerVerified?.seller_verified;
  // Sync country flag select from Redux geoLocation when it resolves
  useEffect(() => {
    if (geoLocation?.countryCode) {
      setCountry(geoLocation.countryCode);
    }
  }, [geoLocation?.countryCode]);

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleQuerySearch();
      }
    };
    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  });
  useEffect(() => {
    dispatch(userActions.getProductData());
  }, [dispatch]);
  return (
    <>
      <div className="es-custom-header-mobile">
        <div className="flex" style={{ marginTop: -23 }}>
          <Link to={ROUTES.HOME}>
            <Logo />
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link to={ROUTES.MESSAGE} style={{ position: "relative", color: "#fff" }}>
              <i className="fa fa-envelope" aria-hidden="true" style={{ fontSize: "18px" }}></i>
              {!!unreadMessageCount && (
                <span className="unread-badge">
                  {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                </span>
              )}
            </Link>
            <div style={{ position: "relative" }} ref={mobileBellRef}>
              <span
                style={{ position: "relative", cursor: "pointer", color: "#fff" }}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <i className="fa fa-bell" aria-hidden="true" style={{ fontSize: "18px" }}></i>
                {!!unreadNotificationCount && (
                  <span className="unread-badge">
                    {unreadNotificationCount > 99
                      ? "99+"
                      : unreadNotificationCount}
                  </span>
                )}
              </span>
              <NotificationsDropdown
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                userToken={userToken}
                onUnreadCountUpdate={setUnreadNotificationCount}
              />
            </div>
            <TextWithIcon
              icon={ICONPATH.cartIcon}
              label=""
              type={"right"}
              color={"#fff"}
              link={ROUTES.CART}
              hasBadge
              clickable
            />
          </div>
        </div>
        <div className="flex lr">
          <BurgerMenu width={"80%"}>
            <Link
              to={ROUTES.HOME}
              style={{ position: "absolute", top: 0, left: 10 }}
            >
              <Logo />
            </Link>
            {user && (
              <TextWithIcon
                label={t("header.myEfAccount")}
                icon={ICONPATH.ChevronDown}
                color="#ee7200"
                size={14}
                hasNoIcon
                weight={700}
                clickable
                link={ROUTES.ACCOUNT}
              />
            )}
            {user && (
              <Link className="menu-item" to={ROUTES.BUYER_ORDER}>
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#fff"
                  link={ROUTES.BUYER_ORDER}
                  label={t("header.order")}
                />
              </Link>
            )}
            {user && (
              <Link className="menu-item" to={ROUTES.INBOX}>
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#fff"
                  link={ROUTES.INBOX}
                  label={t("header.inbox")}
                />
              </Link>
            )}
            {user && (
              <Link className="menu-item" to={ROUTES.SAVEDITEM}>
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#fff"
                  link={ROUTES.SAVEDITEM}
                  label={t("header.savedItems")}
                />
              </Link>
            )}
            {user && (
              <Link className="menu-item" to={ROUTES.SELLER_DASHBOARD}>
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#fff"
                  link={ROUTES.SELLER_DASHBOARD}
                  label={t("header.goToDashboard")}
                />
              </Link>
            )}
            {user ? (
              <Link
                className="menu-item"
                to={ROUTES.HOME}
                onClick={() => dispatch(userActions.logout())}
              >
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#ee7200"
                  link={ROUTES.HOME}
                  label={t("header.logout")}
                />
              </Link>
            ) : (
              <Link className="menu-item" to={ROUTES.LOGIN}>
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#ee7200"
                  link={ROUTES.LOGIN}
                  label={t("login")}
                />
              </Link>
            )}

            <Link to={ROUTES.HOME} className="mobile_logo">
              <Logo />
            </Link>
            <Link className="menu-item" to="#" onClick={onClick}>
              <TextWithIcon
                label={t("header.ourCategories")}
                icon={ICONPATH.ChevronDown}
                color="#ee7200"
                size={14}
                hasNoIcon
                weight={500}
                clickable
                link={"#"}
              />
            </Link>
            {catergoryMenu?.map((el) => {
              return (
                <Link
                  className="menu-item"
                  to={ROUTES.CATEGORY + "/" + el.slug}
                  key={el.id}
                >
                  <Font
                    size={14}
                    weight={500}
                    clickable={true}
                    color="#fff"
                    link={ROUTES.CATEGORY + "/" + el.slug}
                    label={el?.name}
                  />
                </Link>
              );
            })}
            <Link className="menu-item" to="#">
              <TextWithIcon
                label={t("header.others")}
                icon={ICONPATH.ChevronDown}
                color="#ee7200"
                size={14}
                weight={500}
                hasNoIcon
                clickable
                link={"#"}
              />
            </Link>
            <Link className="menu-item" to={ROUTES.MANUFACTURING}>
              <Font
                size={14}
                weight={500}
                clickable={true}
                color="#fff"
                link={ROUTES.MANUFACTURING}
                label={t("header.newManufacturing")}
              />
            </Link>
            <Link className="menu-item" to={ROUTES.ECO_FRIENDLY}>
              <Font
                size={14}
                weight={500}
                clickable={true}
                color="#fff"
                link={ROUTES.ECO_FRIENDLY}
                label={t("header.ecoFriendly")}
              />
            </Link>
            <Link className="menu-item" to={ROUTES.SPONSORSHIP}>
              <Font
                size={14}
                weight={500}
                clickable={true}
                color="#fff"
                link={ROUTES.SPONSORSHIP}
                label={t("header.endlessSponsorship")}
              />
            </Link>
            <Link className="menu-item" to={ROUTES.REGISTER + "?type=seller"}>
              {isAuth ? (
                ""
              ) : (
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#fff"
                  link={ROUTES.REGISTER + "?type=seller"}
                  label={t("header.becomeASeller")}
                />
              )}
            </Link>
            <div
              className="flex"
              style={{
                width: "100%",
                flexDirection: "column",
              }}
            >
              <Link className="menu-item" to="#">
                <Font
                  size={14}
                  weight={500}
                  clickable={true}
                  color="#fff"
                  link={"#"}
                  label={t("Shipping to") + ":"}
                />
              </Link>
              <ReactFlagsSelect
                selected={country}
                onSelect={(code) => setCountry(code)}
                searchable
              />
            </div>
            {/* <Menu menuType={"mobile"} menuData={MENU_DATA} /> */}
          </BurgerMenu>
          <div style={{ position: "relative", width: "100%" }}>
            <Input
              placeholder={t("header.searchPlaceholder")}
              type={"search"}
              onChange={(e) => {
                setQueryParams(e.target.value);
                fetchSuggestions(e.target.value);
              }}
              onClick={() => handleQuerySearch()}
              onBlur={closeSuggestions}
              onKeyDown={handleKeyDown}
            />
            {showSuggestions && (suggestions.length > 0 || recentSearches.length > 0) && (
              <SuggestionsDropdown
                suggestions={suggestions}
                recentSearches={recentSearches}
                onSelect={handleSuggestionClick}
                dropdownRef={dropdownRef}
                t={t}
              />
            )}
          </div>
        </div>
        {/* <div className="logo">
<TextWithIcon
icon={ICONPATH.cartIcon}
label=""
type={"right"}
color={"#fff"}
link={ROUTES.CART}
hasBadge
clickable
/>
</div> */}
      </div>
    </>
  );
};
export const CategoryHeader = ({ onClick, active }) => {
  const { t } = useTranslation();
  const allCategories = useSelector((el) => el?.productMeta?.data?.categories);
  const topLevelCategories = useMemo(() => (allCategories || []).filter((cat) => cat.parent === -1), [allCategories]);

  const [expandedCategory, setExpandedCategory] = useState(null);

  const getSubcategories = (parentId) => {
    return (allCategories || []).filter((cat) => cat.parent === parentId);
  };

  const handleToggleCategory = (catId) => {
    setExpandedCategory(expandedCategory === catId ? null : catId);
  };

  return (
    <div className="es-custom-category_menu">
      <div className="category_sidebar">
        <div className="header">
          <div className="dismiss" onClick={onClick}>
            <img src={ICONPATH.arrowLeft} alt="" />
          </div>
          <Font label={t("header.allCategories")} weight={500} color="#002" size={24} />
        </div>
        <div className="body">
          {topLevelCategories.map((cat) => {
            const subcats = getSubcategories(cat.id);
            const isExpanded = expandedCategory === cat.id;
            return (
              <div className="menu" key={cat.id}>
                <div
                  className="content"
                  onClick={() => handleToggleCategory(cat.id)}
                >
                  <TextWithIcon
                    label={cat.name}
                    icon={subcats.length > 0 ? ICONPATH.ChevronDown : undefined}
                    color="#002"
                    size={20}
                    weight={800}
                    clickable
                    link={ROUTES.CATEGORY + "/" + cat.slug}
                  />
                  {isExpanded && subcats.length > 0 && (
                    <div>
                      {subcats.map((sub) => (
                        <div key={sub.id} style={{ paddingLeft: 16 }}>
                          <Font
                            size={16}
                            weight={500}
                            clickable
                            color="#292929"
                            link={ROUTES.CATEGORY + "/" + sub.slug}
                            label={sub.name}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bd"></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="logo">
        <Link to="/">
          <Logo />
        </Link>
      </div>
    </div>
  );
};
const Header = ({
  click,
  show,
  menuData,
  alert,
  token,
  isLoggedIn,
  ...props
}) => {
  const [isCategory, setCategory] = useState(false);
  const [isDesktop, setDesktop] = useState(window.innerWidth > 876);
  const dispatch = useDispatch();
  const geoDetected = useSelector((el) => el?.geoLocation?.detected);
  const updateMedia = () => {
    setDesktop(window.innerWidth > 876);
  };

  // Detect buyer country via IP on first load
  useEffect(() => {
    if (!geoDetected) {
      dispatch(userActions.detectCountry());
    }
  }, [dispatch, geoDetected]);

  // If user is logged in, save detected country to their profile
  useEffect(() => {
    if (isLoggedIn && geoDetected) {
      dispatch(userActions.saveDetectedCountryToProfile());
    }
  }, [dispatch, isLoggedIn, geoDetected]);

  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    if (alert.type === "alert-success") {
      toast.success(alert.message, {
        toastId: "successid",
      });

      setTimeout(() => {
        dispatch(alertActions.clear());
        if (token) {
          dispatch(userActions.getCartDetails(token));
        }
      }, 1000);
    } else if (alert.type === "alert-danger") {
      toast.error(alert.message, {
        toastId: "errormn",
      });
      setTimeout(() => {
        dispatch(alertActions.clear());
        if (token) {
          dispatch(userActions.getCartDetails(token));
        }
      }, 1000);
    }
    return () => window.removeEventListener("resize", updateMedia);
  }, [alert, dispatch, token]);

  return (
    <>
      <ToastContainer />
      {isDesktop ? (
        <DeskopHeader isLoggedIn={isLoggedIn} />
      ) : (
        <MobileHeader
          isLoggedIn={isLoggedIn}
          onClick={() => setCategory(true)}
        />
      )}
      {isCategory && <CategoryHeader onClick={() => setCategory(false)} />}
    </>
  );
};

Header.propTypes = {
  menuData: PropTypes.array,
};

Header.defaultProps = {
  dropdown: DropDown,
};

function mapStateToProps(state) {
  const { alert, authentication } = state;
  return {
    alert,
    isLoggedIn: authentication?.loggedIn,
    token: authentication.user?.cart_token,
  };
}

const Navbar = connect(mapStateToProps)(Header);

export { Navbar as Header };
